import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form';
import ClayLayout from '@clayui/layout';
import {
    dubaiChamberScopeKey,
    getBasicBusinessDetails,
    personalAndContactDetails
} from '../../../config';
import FormComponent from '../../../generic-components/formComponent/FormComponent';
import CutButton from '../../../generic-components/customCutButton/CutButton';
import { useDispatch, useSelector } from 'react-redux';
import { setBasicInformation } from '../../../redux/slices/basicInformationSlice';
import { RootState } from '../../../redux/store';
import { useDropDownMutation } from '../../../apis/useDropDownMutation';
import { OTPVerifications } from '../../../apis/useApi';
import { toast } from 'react-toastify';
import { getDecodedQueryParam } from '../../../utils/commanUtil';

const BasicInformationForm: React.FC<{
    setStep: React.Dispatch<React.SetStateAction<number>>;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ setStep, setIsLoading }) => {
     const queryParams: any = getDecodedQueryParam();
     const isEPDRequest = Boolean(queryParams?.eventId);
    const {
        control,
        register,
        formState: { errors: formErrors },
        watch,
        handleSubmit,
        setError,
        setValue,
        formState: { errors }
    } = useForm({ mode: 'all' });
    const getBIDProgram = useDropDownMutation(
        `o/headless-admin-list-type/v1.0/list-type-definitions/by-external-reference-code/BID_PROGRAM_REFERENCE`
    );
    const getUAENationalConfirmation = useDropDownMutation(
        `o/headless-admin-list-type/v1.0/list-type-definitions/by-external-reference-code/BID_UAE_NATIONAL_CONFIRMATION`
    );
    const getCountryList = useDropDownMutation(
        `o/headless-admin-list-type/v1.0/list-type-definitions/by-external-reference-code/BID_COUNTRY`
    );

    const getUAEOfficeReference = useDropDownMutation(
        `o/headless-admin-list-type/v1.0/list-type-definitions/by-external-reference-code/BID_OFFICE_REFERENCE`
    );

    const getRegisteredCompanyEmiratesReference = useDropDownMutation(
        `o/headless-admin-list-type/v1.0/list-type-definitions/by-external-reference-code/TRAVEL_REQUEST_UAE_DESTINATION`
    );

    const getDubaiChamberMemberReference = useDropDownMutation(
        `o/headless-admin-list-type/v1.0/list-type-definitions/by-external-reference-code/BID_CHAMBER_MEMBER_REFERENCE`
    );

    const whereYourCompanyHearAboutBIDProgram = watch(
        'whereYourCompanyHearAboutBIDProgram'
    );
    const { basicInformationDetails } = useSelector(
        (state: RootState) => state.basicInformationSlice
    );
    const email = watch('contactWorkEmail');
    const uaeOfficeReference = watch('uaeOfficeReference');
    const registeredCompanyEmiratesReference = watch(
        'registeredCompanyEmiratesReference'
    );
    const dubaiChamberMemberReference = watch('dubaiChamberMemberReference');
    const [dropdownOptions, setDropdownOptions] = useState<any>({});
    const dispatch = useDispatch();

    const onSubmit = async (data: any) => {
        dispatch(setBasicInformation(data));
        setIsLoading(true);
        const response = await OTPVerifications(
            `/v1/otp-service/generate-otp/${email}?scopeKey=${dubaiChamberScopeKey}`
        );
        if (response?.data?.statusCode == 200) {
            setIsLoading(false);
            setStep(1);
            toast.success(response?.data?.statusMessage, {
                theme: 'light',
                autoClose: 2000
            });
        } else {
            setIsLoading(false);
            toast.error(response?.data?.statusMessage, {
                theme: 'light',
                autoClose: 2000
            });
        }
    };

    useEffect(() => {
        const updateDropdownOptions = async () => {
            const whereYourCompanyHearAboutBIDProgram = getBIDProgram;
            const businessOwnedOrCofoundedByUAE = getUAENationalConfirmation;
            const requestorCountryOrOrigin = getCountryList;
            setDropdownOptions((prevData: any) => ({
                ...prevData,
                whereYourCompanyHearAboutBIDProgram,
                businessOwnedOrCofoundedByUAE,
                requestorCountryOrOrigin,
                uaeOfficeReference: getUAEOfficeReference,
                registeredCompanyEmiratesReference:
                    getRegisteredCompanyEmiratesReference,
                dubaiChamberMemberReference: getDubaiChamberMemberReference
            }));
        };
        updateDropdownOptions();
    }, [
        getCountryList,
        getBIDProgram,
        getUAENationalConfirmation,
        getUAEOfficeReference,
        getRegisteredCompanyEmiratesReference,
        getDubaiChamberMemberReference
    ]);

    useEffect(() => {
        Object.entries(basicInformationDetails).forEach(([key, value]) => {
            setValue(key, value);
        });
    }, [basicInformationDetails]);

    const newPersonalAndContactDetails = personalAndContactDetails?.filter(
        (field) => {
            if (field?.name === 'otherValueForBIDProgramReference') {
                return whereYourCompanyHearAboutBIDProgram === 'others';
            }
            return true;
        }
    );

    useEffect(() => {
        if (
            whereYourCompanyHearAboutBIDProgram &&
            whereYourCompanyHearAboutBIDProgram !== 'others'
        ) {
            setValue('otherValueForBIDProgramReference', '');
        }
    }, [whereYourCompanyHearAboutBIDProgram]);

    useEffect(() => {
        if (isEPDRequest) {
            setValue(
                'whereYourCompanyHearAboutBIDProgram',
                'attendedADubaiChambersEventInDubai'
            );
        }
    }, [isEPDRequest]);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="card-body p-0 pr-2 pl-2 personal-info-height">
                <div className="customer-title-container">
                    <h1 className="customer-title px-0 m-0">
                        Personal and Contact details
                    </h1>
                    <div className="ib-hr"></div>
                </div>
                <div className="pt-4">
                    <ClayLayout.Row justify="start" className="content-gap">
                        {newPersonalAndContactDetails?.map((data) => (
                            <ClayLayout.Col
                                key={data.name}
                                xl={6}
                                lg={6}
                                md={6}
                                sm={6}
                                xs={12}
                            >
                                <FormComponent
                                    isPartnerType={true}
                                    placeholder={data.placeholder}
                                    label={data.label}
                                    name={data.name}
                                    register={register}
                                    type={data.type}
                                    control={control}
                                    errors={formErrors}
                                    disabled={
                                        data.name ===
                                            'whereYourCompanyHearAboutBIDProgram' &&
                                        isEPDRequest
                                    }
                                    watch={watch}
                                    setError={setError}
                                    errorTitle={data.errorMessage}
                                    options={dropdownOptions[data.name]}
                                    required={data.required}
                                    component={
                                        data.component as
                                            | 'input'
                                            | 'textarea'
                                            | undefined
                                    }
                                />
                            </ClayLayout.Col>
                        ))}
                    </ClayLayout.Row>
                </div>
                <div className="customer-title-container">
                    <h1 className="customer-title px-0 m-0">
                        Basic Business Details
                    </h1>
                    <div className="ib-hr"></div>
                </div>
                <div className="pt-4">
                    <ClayLayout.Row justify="start" className="content-gap">
                        {getBasicBusinessDetails({
                            uaeOfficeReference,
                            dubaiChamberMemberReference,
                            registeredCompanyEmiratesReference
                        }).map((data: any) => {
                            return (
                                <ClayLayout.Col
                                    key={data.name}
                                    xl={6}
                                    lg={6}
                                    md={6}
                                    sm={6}
                                    xs={12}
                                >
                                    <FormComponent
                                        isPartnerType={true}
                                        placeholder={data.placeholder}
                                        label={data.label}
                                        name={data.name}
                                        register={register}
                                        type={data.type}
                                        control={control}
                                        errors={formErrors}
                                        watch={watch}
                                        setError={setError}
                                        errorTitle={data.errorMessage}
                                        options={dropdownOptions[data.name]}
                                        required={data.required}
                                        component={
                                            data.component as
                                                | 'input'
                                                | 'textarea'
                                                | undefined
                                        }
                                    />
                                </ClayLayout.Col>
                            );
                        })}
                    </ClayLayout.Row>
                </div>
            </div>
            <ClayLayout.ContentSection className="button-group-container mt-4 mb-0 d-flex justify-content-center">
                <CutButton title="Next" buttonType="submit" disabled={false} />
            </ClayLayout.ContentSection>
        </form>
    );
};

export default BasicInformationForm